import type { ReactNode } from 'react';
import { Archive, CornerDownRight, GitBranch, SkipForward } from 'lucide-react';
import { plainOptionLabel, type FactoryQuestionOption } from '@shared/factory-questions';
import { ChoiceCardGroup } from '@/components/ChoiceCard';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { GrowingTextarea } from '@/components/GrowingTextarea';

/** SF-T-308: an item decided some other way than an answer: Skip, Backlog, or (wrap) New branch. */
export type DecisionChoice = 'skip' | 'backlog' | 'branch';

/** What's been picked for one item: options, the Other / steer text, and a choice in place of an answer. */
export interface DecisionValue {
  picked: string[];
  other: string;
  choice?: DecisionChoice | null;
}

/** What each choice says once it's picked. */
const CHOSEN: Record<DecisionChoice, { label: string; icon: typeof SkipForward }> = {
  skip: { label: 'Skipped', icon: SkipForward },
  backlog: { label: 'Backlog', icon: Archive },
  branch: { label: 'New branch', icon: GitBranch },
};

/**
 * SF-T-308: the controls on every item of the AI builder's decisions (FactoryQuestionsCard before it builds,
 * FactorySuggestionsCard at the end of a turn): pick an option (several with `multiSelect`), and/or write in the
 * Other / steer box, always there ("this, but …", or a different way altogether), or decide it otherwise with Skip,
 * Backlog or New branch (`actions`). Skip, Backlog and New branch keep the box for a note. The item's chat goes
 * under it (`chat`).
 */
export function DecisionItemInput({
  label,
  options,
  multiSelect = false,
  recommended,
  value,
  onChange,
  actions = {},
  hints = {},
  placeholder,
  disabled = false,
  maxLength,
  chat,
}: {
  /** The most characters the box takes (a decision's note, SUGGESTION_NOTE_MAX). */
  maxLength?: number;
  /** The item, read out for its controls ("Scope"). */
  label: string;
  options: FactoryQuestionOption[];
  multiSelect?: boolean;
  /** The recommended option's label: marked Recommended. */
  recommended?: string;
  value: DecisionValue | undefined;
  onChange: (change: Partial<DecisionValue>) => void;
  /** The choices offered besides an answer. */
  actions?: Partial<Record<DecisionChoice, boolean>>;
  /** What each choice does, as its hover text and the line once it's picked. */
  hints?: Partial<Record<DecisionChoice, string>>;
  /** The Other / steer box's placeholder. */
  placeholder?: string;
  disabled?: boolean;
  /** SF-T-308: the item's live chat (SuggestionChat), under the controls. */
  chat?: ReactNode;
}) {
  const picked = value?.picked ?? [];
  const choice = value?.choice ?? null;
  const isRecommended = (o: FactoryQuestionOption) => recommended !== undefined && o.label === recommended;
  const title = (o: FactoryQuestionOption) => plainOptionLabel(o.label);
  const offered = (Object.keys(CHOSEN) as DecisionChoice[]).filter((c) => actions[c]);

  if (choice) {
    const { label: chosen, icon: Icon } = CHOSEN[choice];
    return (
      <div className="space-y-1.5">
        <div className="flex flex-wrap items-center gap-2 pl-4 text-sm">
          <CornerDownRight className="size-3.5 shrink-0 text-muted-foreground" aria-hidden />
          <Badge variant="secondary" size="sm">
            <Icon className="size-3" aria-hidden />
            {chosen}
          </Badge>
          {hints[choice] ? <span className="text-xs text-muted-foreground">{hints[choice]}</span> : null}
          <Button size="xs" variant="ghost" className="h-11 sm:h-6" aria-label={`Undo ${chosen} on ${label}`} disabled={disabled} onClick={() => onChange({ choice: null })}>
            Undo
          </Button>
        </div>
        {choice !== 'skip' ? (
          <GrowingTextarea
            aria-label={`${label}: a note for the new task`}
            placeholder="Anything the new task should say (optional)…"
            maxLength={maxLength}
            value={value?.other ?? ''}
            disabled={disabled}
            onChange={(e) => onChange({ other: e.target.value })}
          />
        ) : null}
        {chat}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {options.length && multiSelect ? (
        <div role="group" aria-label={label} className="space-y-1.5">
          {options.map((o) => (
            <label key={o.label} className="flex items-start gap-2 text-sm">
              <Checkbox
                className="mt-0.5"
                checked={picked.includes(o.label)}
                disabled={disabled}
                onCheckedChange={(on) => onChange({ picked: on === true ? [...picked, o.label] : picked.filter((p) => p !== o.label) })}
              />
              <span className="min-w-0">
                {title(o)} {isRecommended(o) ? <RecommendedBadge /> : null}
                {o.description ? <span className="block text-xs text-muted-foreground">{o.description}</span> : null}
              </span>
            </label>
          ))}
        </div>
      ) : options.length ? (
        <ChoiceCardGroup
          size="sm"
          label={label}
          value={picked[0] ?? ''}
          disabled={disabled}
          onValueChange={(v) => onChange({ picked: [v] })}
          options={options.map((o) => ({ value: o.label, title: title(o), description: o.description, badge: isRecommended(o) ? <RecommendedBadge /> : undefined }))}
        />
      ) : null}
      <GrowingTextarea
        aria-label={`${label}: ${options.length ? 'other, or steer the option picked' : 'your answer'}`}
        placeholder={placeholder ?? (options.length ? 'Other, or steer it: “this, but …”' : 'Your answer…')}
        maxLength={maxLength}
        value={value?.other ?? ''}
        disabled={disabled}
        onChange={(e) => onChange({ other: e.target.value })}
      />
      {offered.length || picked.length ? (
        <div className="flex flex-wrap items-center gap-1">
          {offered.map((c) => {
            const { label: name, icon: Icon } = CHOSEN[c];
            return (
              <Button
                key={c}
                size="xs"
                variant="outline"
                className="h-11 sm:h-6"
                aria-label={`${c === 'skip' ? 'Skip' : name}: ${label}`}
                data-hint={hints[c]}
                disabled={disabled}
                // Skip carries nothing, so what was typed goes too (Backlog and New branch keep it as their note).
                onClick={() => onChange({ choice: c, picked: [], ...(c === 'skip' ? { other: '' } : {}) })}
              >
                <Icon className="size-3" aria-hidden />
                {c === 'skip' ? 'Skip' : name}
              </Button>
            );
          })}
          {picked.length ? (
            <Button size="xs" variant="link" className="h-11 text-xs text-muted-foreground sm:h-6" disabled={disabled} onClick={() => onChange({ picked: [] })}>
              Clear the pick
            </Button>
          ) : null}
        </div>
      ) : null}
      {chat}
    </div>
  );
}

function RecommendedBadge() {
  return (
    <Badge variant="ai" size="sm">
      Recommended
    </Badge>
  );
}
